import { API_BASE_URL } from '@/config/api';
import { formatFileSize, formatUploadDate, fileIconName } from './documentsFormat';
import {
  SESSION_EXPIRED_DOCS_MSG,
  isAuthFailureStatus,
  notifyAuthExpired,
} from './apiAuth';

const API = API_BASE_URL;

export { formatFileSize, formatUploadDate, fileIconName };

export class DocumentsApiError extends Error {
  constructor(message, status = 0) {
    super(message);
    this.name = 'DocumentsApiError';
    this.status = status;
    this.authExpired = isAuthFailureStatus(status);
  }
}

export async function fetchDocuments(dealId) {
  const res = await fetch(`${API}/api/deals/${dealId}/documents`, { credentials: 'include' });
  if (!res.ok) {
    if (isAuthFailureStatus(res.status)) {
      notifyAuthExpired();
      throw new DocumentsApiError(SESSION_EXPIRED_DOCS_MSG, res.status);
    }
    throw new DocumentsApiError('Failed to load documents', res.status);
  }
  return res.json();
}

export async function uploadDocument(dealId, file, onProgress) {
  const maxBytes = 25 * 1024 * 1024;
  if (file.size > maxBytes) {
    throw new Error('File exceeds 25 MB limit');
  }

  const form = new FormData();
  form.append('file', file);
  return new Promise((resolve, reject) => {
    const xhr = new XMLHttpRequest();
    xhr.open('POST', `${API}/api/deals/${dealId}/documents`);
    xhr.withCredentials = true;
    xhr.timeout = 300000; // 5 min — large PDFs on slow connections
    xhr.upload.onprogress = (e) => {
      if (e.lengthComputable && onProgress) onProgress(Math.round((e.loaded / e.total) * 100));
    };
    xhr.onload = () => {
      if (xhr.status === 0) {
        reject(new Error('Upload interrupted — check your connection or try a smaller file'));
        return;
      }
      if (xhr.status >= 200 && xhr.status < 300) {
        try { resolve(JSON.parse(xhr.responseText)); } catch { reject(new Error('Invalid response')); }
      } else {
        let msg = `Upload failed (${xhr.status})`;
        try {
          const body = JSON.parse(xhr.responseText);
          const detail = body.detail;
          if (Array.isArray(detail)) {
            msg = detail.map((item) => item.msg || item).join('; ');
          } else if (detail) {
            msg = detail;
          }
        } catch { /* ignore */ }
        reject(new Error(msg));
      }
    };
    xhr.onerror = () => reject(new Error('Network error — upload may have timed out. Try again or use a smaller file.'));
    xhr.ontimeout = () => reject(new Error('Upload timed out — try a smaller file or faster connection'));
    xhr.send(form);
  });
}

export async function deleteDocument(dealId, docId) {
  const res = await fetch(`${API}/api/deals/${dealId}/documents/${docId}`, {
    method: 'DELETE',
    credentials: 'include',
  });
  if (!res.ok) {
    if (isAuthFailureStatus(res.status)) {
      notifyAuthExpired();
      throw new DocumentsApiError(SESSION_EXPIRED_DOCS_MSG, res.status);
    }
    throw new DocumentsApiError('Delete failed', res.status);
  }
  return res.json();
}

export async function fetchDocumentMeta(dealId, docId) {
  const res = await fetch(`${API}/api/deals/${dealId}/documents/${docId}`, { credentials: 'include' });
  if (!res.ok) {
    if (isAuthFailureStatus(res.status)) {
      notifyAuthExpired();
      throw new DocumentsApiError(SESSION_EXPIRED_DOCS_MSG, res.status);
    }
    throw new DocumentsApiError('Document not found', res.status);
  }
  return res.json();
}

export function documentFileUrl(dealId, docId) {
  return `${API}/api/deals/${dealId}/documents/${docId}/file`;
}

export async function downloadDocumentFile(dealId, docId, fileName) {
  const res = await fetch(documentFileUrl(dealId, docId), { credentials: 'include' });
  if (!res.ok) {
    if (isAuthFailureStatus(res.status)) {
      notifyAuthExpired();
      throw new DocumentsApiError(SESSION_EXPIRED_DOCS_MSG, res.status);
    }
    throw new DocumentsApiError('Download failed', res.status);
  }
  const blob = await res.blob();
  const url = URL.createObjectURL(blob);
  const a = document.createElement('a');
  a.href = url;
  a.download = fileName;
  document.body.appendChild(a);
  a.click();
  a.remove();
  URL.revokeObjectURL(url);
}
